import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v3";
import { useBulkAddFlag } from "@/api/assurance/bulk-add-flag";
import {
  type BulkFlagData,
  useBulkApproveFlags,
  useBulkEditFlags,
  useBulkRejectFlags,
  useBulkVideoFlags,
} from "@/api/assurance/bulk-flags";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RISK_TIERS, RISK_TYPES } from "@/lib/assurance/risk-types";
import { error, success, warning } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";
import {
  getReviewRiskActionIcon,
  getReviewRiskActionLabel,
  REVIEW_RISK_ACTIONS,
  type ReviewRiskAction,
} from "./review-risk-dropdown";

interface ReviewRiskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedVideos: string[];
  initialAction?: ReviewRiskAction;
  onSuccess: () => void;
}

export const ReviewRiskModal = ({
  open,
  onOpenChange,
  selectedVideos,
  initialAction,
  onSuccess,
}: ReviewRiskModalProps) => {
  const [selectedAction, setSelectedAction] = useState<ReviewRiskAction | undefined>(initialAction);

  // Fetch pending flags for selected videos (only when action is approve/reject/edit)
  const needsFlags = selectedAction && selectedAction !== "add_new_risk";
  const { data: pendingFlags = [], isLoading: isFetchingFlags } = useBulkVideoFlags(selectedVideos, needsFlags && open);

  // Mutations
  const { mutateAsync: approveFlags, isPending: isApproving } = useBulkApproveFlags();
  const { mutateAsync: rejectFlags, isPending: isRejecting } = useBulkRejectFlags();
  const { mutateAsync: editFlags, isPending: isEditing } = useBulkEditFlags();
  const { mutateAsync: addFlags, isPending: isAdding } = useBulkAddFlag();

  // Separate loading states for better UX
  const mutationPending = isApproving || isRejecting || isEditing || isAdding;
  const isLoading = mutationPending || isFetchingFlags;

  // Reset action when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedAction(initialAction);
    }
  }, [open, initialAction]);

  // Update selected action when initialAction changes
  useEffect(() => {
    if (initialAction) {
      setSelectedAction(initialAction);
    }
  }, [initialAction]);

  const handleClose = () => {
    onOpenChange(false);
    setSelectedAction(undefined);
  };

  const handleActionChange = (action: ReviewRiskAction) => {
    setSelectedAction(action);
  };

  const handleApprove = async () => {
    if (pendingFlags.length === 0) return;

    try {
      const result = await approveFlags(pendingFlags as BulkFlagData[]);

      if (!result) {
        error("Failed to approve risks");
        return;
      }

      const successCount = result.successful.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        success(`Approved ${pluralize(successCount, "risk", "risks")}`);
      } else if (successCount > 0) {
        warning(`Partially completed: ${successCount} approved, ${failedCount} failed`);
      } else {
        error("Failed to approve risks");
        return;
      }

      if (successCount > 0) {
        onSuccess();
        handleClose();
      }
    } catch (err) {
      error("Failed to approve risks");
      console.error("Error approving flags:", err);
    }
  };

  const handleReject = async (explanation: string) => {
    if (pendingFlags.length === 0) return;

    try {
      const result = await rejectFlags({ flags: pendingFlags as BulkFlagData[], feedbackReason: explanation });

      if (!result) {
        error("Failed to reject risks");
        return;
      }

      const successCount = result.successful.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        success(`Rejected ${pluralize(successCount, "risk", "risks")}`);
      } else if (successCount > 0) {
        warning(`Partially completed: ${successCount} rejected, ${failedCount} failed`);
      } else {
        error("Failed to reject risks");
        return;
      }

      if (successCount > 0) {
        onSuccess();
        handleClose();
      }
    } catch (err) {
      error("Failed to reject risks");
      console.error("Error rejecting flags:", err);
    }
  };

  const handleEdit = async (data: {
    riskTier: string;
    riskType: string;
    flagExplanation: string;
    feedback: string;
  }) => {
    if (pendingFlags.length === 0) return;

    try {
      const result = await editFlags({
        flags: pendingFlags as BulkFlagData[],
        riskTier: Number.parseInt(data.riskTier),
        riskTypeShort: data.riskType,
        explanation: data.flagExplanation,
        feedbackReason: data.feedback,
      });

      if (!result) {
        error("Failed to edit risks");
        return;
      }

      const successCount = result.successful.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        success(`Edited ${pluralize(successCount, "risk", "risks")}`);
      } else if (successCount > 0) {
        warning(`Partially completed: ${successCount} edited, ${failedCount} failed`);
      } else {
        error("Failed to edit risks");
        return;
      }

      if (successCount > 0) {
        onSuccess();
        handleClose();
      }
    } catch (err) {
      error("Failed to edit risks");
      console.error("Error editing flags:", err);
    }
  };

  const handleAdd = async (data: { riskTier: string; riskType: string; explanation: string }) => {
    try {
      const result = await addFlags({
        videoIds: selectedVideos,
        riskTier: Number.parseInt(data.riskTier),
        riskTypeShort: data.riskType,
        explanation: data.explanation,
      });

      if (!result) {
        error("Failed to add risks");
        return;
      }

      const successCount = result.successful.length;
      const failedCount = result.failed.length;

      if (failedCount === 0) {
        success(`Added risk to ${pluralize(successCount, "video", "videos")}`);
      } else if (successCount > 0) {
        warning(
          `Added risk to ${pluralize(successCount, "video", "videos")}, but failed for ${pluralize(failedCount, "video", "videos")}`,
        );
      } else {
        error(`Failed to add risk to ${pluralize(failedCount, "video", "videos")}`);
        return;
      }

      if (successCount > 0) {
        onSuccess();
        handleClose();
      }
    } catch (err) {
      error("Failed to add risks");
      console.error("Error adding flags:", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Review Risk</DialogTitle>
        </DialogHeader>

        {/* Action selector - always visible */}
        <div className="space-y-2">
          <Select value={selectedAction} onValueChange={(v) => handleActionChange(v as ReviewRiskAction)}>
            <SelectTrigger>
              <SelectValue placeholder="Select option">
                {selectedAction && (
                  <div className="flex items-center gap-2">
                    {(() => {
                      const Icon = getReviewRiskActionIcon(selectedAction);
                      return <Icon className="w-4 h-4" />;
                    })()}
                    <span>
                      {getReviewRiskActionLabel(selectedAction)} in{" "}
                      {pluralize(selectedVideos.length, "Video", "Videos")}
                    </span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="[&_[role=option]]:pl-2 [&_[role=option]]:pr-8 [&_[role=option]>span:first-child]:left-auto [&_[role=option]>span:first-child]:right-2">
              {REVIEW_RISK_ACTIONS.map((action) => {
                const Icon = getReviewRiskActionIcon(action);
                return (
                  <SelectItem key={action} value={action}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>{getReviewRiskActionLabel(action)}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Dynamic form content based on selected action */}
        {selectedAction === "approve_identified_risks" && (
          <ApproveForm
            mutationPending={isApproving}
            isLoading={isLoading}
            onSubmit={handleApprove}
            onCancel={handleClose}
            flagCount={pendingFlags.length}
            isFetchingFlags={isFetchingFlags}
          />
        )}

        {selectedAction === "reject_identified_risks" && (
          <RejectForm
            mutationPending={isRejecting}
            isLoading={isLoading}
            onSubmit={handleReject}
            onCancel={handleClose}
          />
        )}

        {selectedAction === "edit_identified_risks" && (
          <EditForm mutationPending={isEditing} isLoading={isLoading} onSubmit={handleEdit} onCancel={handleClose} />
        )}

        {selectedAction === "add_new_risk" && (
          <AddForm mutationPending={isAdding} isLoading={isLoading} onSubmit={handleAdd} onCancel={handleClose} />
        )}

        {/* Show footer with cancel button if no action selected */}
        {!selectedAction && (
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" color="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" disabled>
              Remove Risk
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Approve Form
const ApproveForm = ({
  mutationPending,
  isLoading,
  onSubmit,
  onCancel,
  flagCount,
  isFetchingFlags,
}: {
  mutationPending: boolean;
  isLoading: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  flagCount: number;
  isFetchingFlags: boolean;
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 bg-warning-primary rounded-lg">
        <p className="text-sm">This will add the risk and will then need to be actioned accordingly.</p>
      </div>

      {isFetchingFlags && <p className="text-sm text-tertiary">Loading flags...</p>}

      {!isFetchingFlags && flagCount === 0 && (
        <p className="text-sm text-tertiary">No pending risks found in the selected videos.</p>
      )}

      <DialogFooter>
        <Button type="button" color="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="button" onClick={onSubmit} disabled={isLoading || flagCount === 0}>
          {mutationPending ? "Approving..." : "Approve Risks"}
        </Button>
      </DialogFooter>
    </div>
  );
};

// Reject Form
const RejectForm = ({
  mutationPending,
  isLoading,
  onSubmit,
  onCancel,
}: {
  mutationPending: boolean;
  isLoading: boolean;
  onSubmit: (explanation: string) => void;
  onCancel: () => void;
}) => {
  const form = useForm({
    resolver: zodResolver(z.object({ explanation: z.string().min(1, "Explanation is required") })),
    defaultValues: { explanation: "" },
  });

  const handleSubmit = (data: { explanation: string }) => {
    onSubmit(data.explanation);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="explanation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Explain why this risk is incorrect..."
                  rows={4}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" color="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {mutationPending ? "Rejecting..." : "Reject Risks"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

// Edit Form
const EditForm = ({
  mutationPending,
  isLoading,
  onSubmit,
  onCancel,
}: {
  mutationPending: boolean;
  isLoading: boolean;
  onSubmit: (data: { riskTier: string; riskType: string; flagExplanation: string; feedback: string }) => void;
  onCancel: () => void;
}) => {
  const form = useForm({
    resolver: zodResolver(
      z.object({
        riskTier: z.enum(["1", "2"]).optional(),
        riskType: z.string().min(1, "Risk type is required"),
        flagExplanation: z.string(),
        feedback: z.string().min(1, "Feedback is required"),
      }),
    ),
    defaultValues: {
      riskTier: undefined,
      riskType: "",
      flagExplanation: "",
      feedback: "",
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          if (!data.riskTier) return;
          onSubmit({ ...data, riskTier: data.riskTier });
        })}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="riskTier"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Risk Tier</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isLoading}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Risk tier" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {RISK_TIERS.map((tier) => (
                    <SelectItem key={tier.value} value={tier.value}>
                      {tier.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="riskType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Risk</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isLoading}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Risk" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {RISK_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="flagExplanation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Flag Explanation</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="No hashtags or overlay text indicating that it is an ad."
                  rows={2}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="feedback"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Feedback</FormLabel>
              <FormControl>
                <Textarea placeholder="Explain why this risk was changed..." rows={3} {...field} disabled={isLoading} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" color="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {mutationPending ? "Editing..." : "Edit Risks"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

// Add New Risk Form
const AddForm = ({
  mutationPending,
  isLoading,
  onSubmit,
  onCancel,
}: {
  mutationPending: boolean;
  isLoading: boolean;
  onSubmit: (data: { riskTier: string; riskType: string; explanation: string }) => void;
  onCancel: () => void;
}) => {
  const form = useForm({
    resolver: zodResolver(
      z.object({
        riskTier: z.enum(["1", "2"]).optional(),
        riskType: z.string().min(1, "Risk type is required"),
        explanation: z.string().min(1, "Explanation is required"),
      }),
    ),
    defaultValues: {
      riskTier: undefined,
      riskType: "",
      explanation: "",
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          if (!data.riskTier) return;
          onSubmit({ ...data, riskTier: data.riskTier });
        })}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="riskTier"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Risk Tier</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isLoading}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Risk tier" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {RISK_TIERS.map((tier) => (
                    <SelectItem key={tier.value} value={tier.value}>
                      {tier.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="riskType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Risk</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isLoading}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Risk" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {RISK_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="explanation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Explain why this content is a risk..."
                  rows={4}
                  {...field}
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" color="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {mutationPending ? "Adding..." : "Add Risks"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
